import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, Copy, Sparkles, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
interface ImagePopupProps {
  image: {
    id: string;
    prompt: string;
    image_url: string;
    b64_json?: string;
    model: string;
    size: string;
    quality?: string;
    style?: string;
    created_at: string;
    original_settings?: any;
  };
  children: React.ReactNode;
  onCopySettings: (settings: any) => void;
  onUseSettings?: (settings: any) => void;
}
const ImagePopup = ({
  image,
  children,
  onCopySettings,
  onUseSettings
}: ImagePopupProps) => {
  const downloadImage = async () => {
    try {
      // Detect MIME type from base64
      const detectMime = (b64: string) => {
        if (b64.startsWith('/9j/')) return 'image/jpeg';
        if (b64.startsWith('iVBORw')) return 'image/png';
        return 'image/jpeg';
      };
      const getExt = (mime: string) => mime.includes('jpeg') || mime.includes('jpg') ? 'jpg' : 'png';
      
      let blob;
      let mimeType = 'image/jpeg';
      let finalImageUrl = '';
      
      // Build URL with correct MIME type
      if (image.b64_json) {
        mimeType = detectMime(image.b64_json);
        finalImageUrl = `data:${mimeType};base64,${image.b64_json}`;
      } else {
        finalImageUrl = image.image_url || '';
      }
      
      if (finalImageUrl.startsWith('data:image/')) {
        // Handle base64 data URLs directly
        const base64Data = finalImageUrl.split(',')[1];
        mimeType = finalImageUrl.match(/data:([^;]+)/)?.[1] || 'image/jpeg';
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let j = 0; j < binaryString.length; j++) {
          bytes[j] = binaryString.charCodeAt(j);
        }
        blob = new Blob([bytes], { type: mimeType });
      } else {
        const response = await fetch(finalImageUrl);
        blob = await response.blob();
        mimeType = blob.type || 'image/jpeg';
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${image.prompt.slice(0, 30).replace(/[^a-z0-9]/gi, '_')}-${Date.now()}.${getExt(mimeType)}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Image downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download image');
    }
  };
  const handleCopySettings = () => {
    const settings = {
      prompt: image.prompt,
      model: image.model,
      size: image.size,
      quality: image.quality || 'standard',
      style: image.style || 'vivid',
      ...image.original_settings
    };
    console.log('Copying settings:', settings);

    // Use the passed onUseSettings if available, otherwise use onCopySettings
    if (onUseSettings) {
      onUseSettings(settings);
    } else {
      onCopySettings(settings);
    }
    toast.success('Settings copied! Switch to Generate tab to use them.', {
      description: 'All settings have been applied automatically.',
      duration: 3000
    });
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return <Dialog>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] w-[90vw] bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 border border-purple-500/30 overflow-y-auto backdrop-blur-xl">
        {/* Background effects matching hero section */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-purple-600/10 pointer-events-none"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:50px_50px] pointer-events-none"></div>
        
        <DialogHeader className="relative z-10">
          <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent flex items-center gap-2">
            <ImageIcon className="w-6 h-6 text-blue-400" />
            Image Details
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 relative z-10">
          {/* Image Display with enhanced styling */}
          <div className="space-y-4">
            <div className="flex justify-center">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl blur-xl group-hover:blur-2xl transition-all duration-300"></div>
                <img 
                  src={image.image_url || (image.b64_json ? `data:image/png;base64,${image.b64_json}` : '')} 
                  alt={image.prompt}
                  className="relative max-w-full max-h-[400px] object-contain rounded-xl bg-black/20 backdrop-blur-sm shadow-2xl border border-white/10" 
                  style={{
                    maxWidth: '100%',
                    height: 'auto'
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iIzM3NDE0OSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5Q0E0QUYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBOb3QgRm91bmQ8L3RleHQ+PC9zdmc+';
                  }}
                />
              </div>
            </div>
            
            {/* Action buttons with hero theme */}
            <div className="flex gap-3 justify-center flex-wrap">
              <Button onClick={downloadImage} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-blue-500/25 transition-all duration-300 hover:scale-105" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Download Image
              </Button>
              <Button onClick={handleCopySettings} className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg hover:shadow-purple-500/25 transition-all duration-300 hover:scale-105" size="sm">
                <Copy className="w-4 h-4 mr-2" />
                <Sparkles className="w-4 h-4 mr-1" />
                Use These Settings
              </Button>
            </div>
          </div>
          
          {/* Details section with enhanced styling */}
          <div className="space-y-4 bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10">
            {/* Prompt section */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-400" />
                Prompt
              </h3>
              <div className="bg-gradient-to-r from-slate-800/50 to-purple-900/20 p-4 rounded-xl border border-purple-500/20">
                <p className="text-gray-100 text-sm leading-relaxed max-h-32 overflow-y-auto">
                  {image.prompt}
                </p>
              </div>
            </div>
            
            {/* Settings grid with enhanced styling */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-gradient-to-r from-slate-800/50 to-blue-900/20 p-4 rounded-xl border border-blue-500/20">
                <h4 className="text-sm font-medium text-blue-200 mb-2">Size</h4>
                <p className="text-blue-100">{image.size}</p>
              </div>
              
              {image.quality && (
                <div className="bg-gradient-to-r from-slate-800/50 to-green-900/20 p-4 rounded-xl border border-green-500/20">
                  <h4 className="text-sm font-medium text-green-200 mb-2">Quality</h4>
                  <p className="text-green-100">{image.quality}</p>
                </div>
              )}
              
              {image.style && (
                <div className="bg-gradient-to-r from-slate-800/50 to-pink-900/20 p-4 rounded-xl border border-pink-500/20">
                  <h4 className="text-sm font-medium text-pink-200 mb-2">Style</h4>
                  <p className="text-pink-100">{image.style}</p>
                </div>
              )}
            </div>
            
            {/* Creation date */}
            <div className="bg-gradient-to-r from-slate-800/50 to-gray-900/20 p-4 rounded-xl border border-gray-500/20">
              <h4 className="text-sm font-medium text-gray-200 mb-2">Created</h4>
              <p className="text-gray-100">{formatDate(image.created_at)}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>;
};
export default ImagePopup;